import {
  type AutomationTitleMode,
  resolveAutomationName,
  resolveAutomationTitleWrite,
} from '@blocksuite/affine-block-streamlain';

type AutomationTitleIdentity = {
  title?: unknown;
  titleMode?: AutomationTitleMode | null;
  workflowName?: string | null;
};

type MutableTextLike = {
  yText?: { length: number; doc?: unknown };
  length?: number;
  replace: (index: number, length: number, text: string) => void;
  toString?: () => string;
};

type BlockModelLike = { props: Record<string, unknown> };

function isMutableText(value: unknown): value is MutableTextLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as MutableTextLike).replace === 'function'
  );
}

function getTextLength(text: MutableTextLike): number {
  if (typeof text.yText?.length === 'number') {
    return text.yText.length;
  }
  if (typeof text.length === 'number') {
    return text.length;
  }
  return text.toString?.().length ?? 0;
}

/** Event name dispatched after title write to notify canvas blocks */
export const AUTOMATION_TITLE_CHANGED = 'automation-title-changed';

/**
 * Resolves the name shown by the active Automation detail sidebar.
 * Discovery is already block-scoped and reactive, so it is the preferred
 * display source. The model config remains the loading/cross-doc fallback.
 */
export function resolveAutomationDetailTitle(
  discoveredTitle: string | null | undefined,
  identity: AutomationTitleIdentity,
  defaultTitle: string
): string {
  return (
    discoveredTitle?.trim() || resolveAutomationName(identity, defaultTitle)
  );
}

/** Writes the automation title into its Y.Text title prop and dispatches a window event so canvas block components can re-render. */
export function writeAutomationTitle(
  blockModel: BlockModelLike | null | undefined,
  nextTitle: string,
  source: 'user' | 'workflow' = 'user'
): boolean {
  if (!blockModel) return false;

  const next = resolveAutomationTitleWrite(
    {
      title: blockModel.props.title,
      titleMode: blockModel.props.titleMode as AutomationTitleMode | undefined,
      workflowName: blockModel.props.workflowName as string | null | undefined,
    },
    nextTitle,
    source
  );
  if (!next) return false;

  const titleProp = blockModel.props.title;
  if (!isMutableText(titleProp)) {
    throw new Error('writeAutomationTitle: automation title is not a Y.Text');
  }

  // If Y.Text is detached (deleted block), skip writing.
  if (titleProp.yText && !titleProp.yText.doc) {
    return false;
  }

  blockModel.props.titleMode = next.titleMode;
  titleProp.replace(0, getTextLength(titleProp), next.title);
  window.dispatchEvent(new CustomEvent(AUTOMATION_TITLE_CHANGED));
  return true;
}
